import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, 
  LineChart, Line, CartesianGrid, PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import { toast } from '../lib/toast';
import {
  LayoutDashboard,
  ShoppingCart,
  Grid3X3,
  Package,
  Brain,
  DollarSign,
  TrendingUp,
  TrendingDown,
  ShoppingBag,
  AlertTriangle,
  Utensils,
  Truck,
  Clock,
  RefreshCw,
  ChefHat,
  MapPin,
  Calendar,
  Users,
  Zap,
  Target,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import Sidebar from '../components/layout/Sidebar';

const COLORS = ['#f97316', '#10b981', '#6366f1', '#94a3b8'];

const Dashboard = () => {
  const { api, user } = useAuth();
  const { branchId, branchName } = useBranch();
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [salesByType, setSalesByType] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // New states for advanced dashboard
  const [selectedPeriod, setSelectedPeriod] = useState('week');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCustomDates, setShowCustomDates] = useState(false);
  const [advancedStats, setAdvancedStats] = useState(null);
  const [loadingAdvanced, setLoadingAdvanced] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, [branchId]); // Recargar cuando cambie la sucursal
  
  useEffect(() => {
    if (selectedPeriod !== 'custom') {
      fetchAdvancedStats(selectedPeriod);
      fetchChartData(selectedPeriod);
    }
  }, [selectedPeriod, branchId]);

  // Fetch chart data based on period
  const fetchChartData = async (period, startDate = null, endDate = null) => {
    try {
      const branchQuery = branchId ? `&branch_id=${branchId}` : '';
      
      // Calculate days based on period
      let days = 7;
      if (period === 'today') days = 1;
      else if (period === 'week') days = 7;
      else if (period === 'month') days = 30;
      else if (period === 'year') days = 365;
      
      let chartUrl = `/dashboard/sales-chart?days=${days}${branchQuery}`;
      let typeUrl = `/dashboard/sales-by-type?days=${days}${branchQuery}`;
      
      if (startDate && endDate) {
        // For custom dates, calculate days difference
        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
        chartUrl = `/dashboard/sales-chart?days=${diffDays}${branchQuery}&start_date=${startDate}&end_date=${endDate}`;
        typeUrl = `/dashboard/sales-by-type?days=${diffDays}${branchQuery}&start_date=${startDate}&end_date=${endDate}`;
      }
      
      const [chartRes, typeRes] = await Promise.all([
        api.get(chartUrl),
        api.get(typeUrl)
      ]);
      
      setChartData(chartRes.data);
      setSalesByType(typeRes.data);
    } catch (error) {
      console.error('Error fetching chart data:', error);
    }
  };

  const fetchAdvancedStats = async (period, startDate = null, endDate = null) => {
    setLoadingAdvanced(true);
    try {
      const branchQuery = branchId ? `&branch_id=${branchId}` : '';
      let url = `/dashboard/advanced-stats?period=${period}${branchQuery}`;
      if (startDate && endDate) {
        url += `&start_date=${startDate}&end_date=${endDate}`;
      }
      const res = await api.get(url);
      setAdvancedStats(res.data);
    } catch (error) {
      console.error('Error fetching advanced stats:', error);
    } finally {
      setLoadingAdvanced(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      // Fetch each endpoint separately to handle individual failures
      const fetchWithFallback = async (endpoint, fallback) => {
        try {
          const res = await api.get(endpoint);
          return res.data;
        } catch (error) {
          console.error(`Error fetching ${endpoint}:`, error);
          return fallback;
        }
      };

      const branchQuery = branchId ? `branch_id=${branchId}` : '';
      const [statsData, chartData, topData, typeData, recentData] = await Promise.all([
        fetchWithFallback(`/dashboard/stats?${branchQuery}`, { today_sales: 0, today_orders: 0, avg_ticket: 0, low_stock_count: 0 }),
        fetchWithFallback(`/dashboard/sales-chart?days=7&${branchQuery}`, []),
        fetchWithFallback(`/dashboard/top-products?limit=5&${branchQuery}`, []),
        fetchWithFallback(`/dashboard/sales-by-type?days=7&${branchQuery}`, []),
        fetchWithFallback(`/dashboard/recent-orders?limit=5&${branchQuery}`, [])
      ]);

      setStats(statsData);
      setChartData(chartData);
      setTopProducts(topData);
      setSalesByType(typeData);
      setRecentOrders(recentData);
    } catch (error) {
      console.error('Dashboard error:', error);
      toast.error('Error al cargar datos del dashboard');
    } finally {
      setLoading(false);
    }
  };

  const kpiCards = [
    {
      title: 'Ventas de Hoy',
      value: stats ? `$${stats.today_sales.toLocaleString()}` : '-',
      icon: DollarSign,
      color: 'text-orange-500',
      bgColor: 'bg-orange-500/10'
    },
    {
      title: 'Órdenes de Hoy',
      value: stats?.today_orders || 0,
      icon: ShoppingBag,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10'
    },
    {
      title: 'Ticket Promedio',
      value: stats ? `$${stats.avg_ticket.toLocaleString()}` : '-',
      icon: TrendingUp,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10'
    },
    {
      title: 'Bajo Stock',
      value: stats?.low_stock_count || 0,
      icon: AlertTriangle,
      color: stats?.low_stock_count > 0 ? 'text-red-500' : 'text-gray-400',
      bgColor: stats?.low_stock_count > 0 ? 'bg-red-500/10' : 'bg-gray-500/10'
    }
  ];

  const getOrderTypeIcon = (type) => {
    switch(type) {
      case 'dine_in': return <Utensils className="w-4 h-4" />;
      case 'takeout': return <Package className="w-4 h-4" />;
      case 'delivery': return <Truck className="w-4 h-4" />;
      default: return <ShoppingBag className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'preparing': return 'bg-blue-100 text-blue-800';
      case 'ready': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pendiente',
      preparing: 'Preparando',
      ready: 'Listo',
      completed: 'Completado',
      cancelled: 'Cancelado'
    };
    return labels[status] || status;
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-background flex">
      <Sidebar />
      
      <main className="flex-1 md:ml-64 p-4 md:p-8 pt-20 md:pt-8">
        <div className="max-w-7xl mx-auto">
          {/* Header with Period Filter */}
          <div className="flex flex-col gap-4 mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-chivo font-extrabold" data-testid="dashboard-title">
                  Panel de Control
                </h1>
                <p className="text-muted-foreground flex items-center gap-2">
                  Bienvenido, {user?.name || 'Usuario'}
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                    <MapPin className="w-3 h-3" />
                    {branchName}
                  </span>
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={fetchDashboardData} disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
            </div>
            
            {/* Period Filter */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground mr-2">Período:</span>
              {[
                { value: 'today', label: 'Hoy' },
                { value: 'week', label: 'Semana' },
                { value: 'month', label: 'Mes' },
                { value: 'year', label: 'Año' },
                { value: 'custom', label: 'Personalizado' }
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    setSelectedPeriod(option.value);
                    if (option.value === 'custom') {
                      setShowCustomDates(true);
                    } else {
                      setShowCustomDates(false);
                    }
                  }}
                  className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                    selectedPeriod === option.value
                      ? 'bg-orange-500 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {option.label}
                </button>
              ))}
              
              {showCustomDates && (
                <div className="flex items-center gap-2 ml-4 p-2 bg-orange-50 rounded-lg">
                  <Calendar className="w-4 h-4 text-orange-500" />
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                  <span className="text-sm text-slate-500">a</span>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                  <Button
                    size="sm"
                    onClick={() => {
                      if (customStartDate && customEndDate) {
                        fetchAdvancedStats('custom', customStartDate, customEndDate);
                        fetchChartData('custom', customStartDate, customEndDate);
                      } else {
                        toast.error('Selecciona ambas fechas');
                      }
                    }}
                    className="bg-orange-500 hover:bg-orange-600"
                  >
                    Aplicar
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Advanced KPI Cards with Comparisons */}
          {advancedStats && (
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6 mb-8">
              {/* Sales */}
              <Card className="border border-border/50 hover:border-orange-500/50 transition-colors">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Ventas del Período</p>
                      <p className="text-2xl font-chivo font-extrabold">${advancedStats.current_period.sales.toLocaleString()}</p>
                      <div className={`flex items-center gap-1 text-xs mt-1 ${advancedStats.changes.sales_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {advancedStats.changes.sales_percent >= 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                        {Math.abs(advancedStats.changes.sales_percent)}% vs anterior
                      </div>
                    </div>
                    <div className="p-2 rounded-lg bg-orange-500/10">
                      <DollarSign className="w-5 h-5 text-orange-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Orders */}
              <Card className="border border-border/50 hover:border-green-500/50 transition-colors">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Órdenes</p>
                      <p className="text-2xl font-chivo font-extrabold">{advancedStats.current_period.orders}</p>
                      <div className={`flex items-center gap-1 text-xs mt-1 ${advancedStats.changes.orders_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {advancedStats.changes.orders_percent >= 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                        {Math.abs(advancedStats.changes.orders_percent)}% vs anterior
                      </div>
                    </div>
                    <div className="p-2 rounded-lg bg-green-500/10">
                      <ShoppingBag className="w-5 h-5 text-green-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Avg Ticket */}
              <Card className="border border-border/50 hover:border-blue-500/50 transition-colors">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Ticket Promedio</p>
                      <p className="text-2xl font-chivo font-extrabold">${advancedStats.current_period.avg_ticket.toLocaleString()}</p>
                      <div className={`flex items-center gap-1 text-xs mt-1 ${advancedStats.changes.ticket_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {advancedStats.changes.ticket_percent >= 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                        {Math.abs(advancedStats.changes.ticket_percent)}% vs anterior
                      </div>
                    </div>
                    <div className="p-2 rounded-lg bg-blue-500/10">
                      <TrendingUp className="w-5 h-5 text-blue-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Unique Customers */}
              <Card className="border border-border/50 hover:border-purple-500/50 transition-colors">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Clientes Únicos</p>
                      <p className="text-2xl font-chivo font-extrabold">{advancedStats.current_period.unique_customers}</p>
                      <p className="text-xs text-muted-foreground mt-1">En el período</p>
                    </div>
                    <div className="p-2 rounded-lg bg-purple-500/10">
                      <Users className="w-5 h-5 text-purple-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Best Hour */}
              <Card className="border border-border/50 hover:border-amber-500/50 transition-colors">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Mejor Hora</p>
                      <p className="text-2xl font-chivo font-extrabold">{advancedStats.insights.best_hour_formatted || '-'}</p>
                      <p className="text-xs text-green-600 mt-1">${advancedStats.insights.best_hour_sales?.toLocaleString() || 0}</p>
                    </div>
                    <div className="p-2 rounded-lg bg-amber-500/10">
                      <Clock className="w-5 h-5 text-amber-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Original KPI Cards (Today's quick view) */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
            {kpiCards.map((kpi, idx) => (
              <Card key={idx} className="border border-border/50 hover:border-orange-500/50 transition-colors" data-testid={`kpi-card-${idx}`}>
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs md:text-sm text-muted-foreground mb-1">{kpi.title}</p>
                      <p className="text-xl md:text-3xl font-chivo font-extrabold">{kpi.value}</p>
                    </div>
                    <div className={`p-2 md:p-3 rounded-lg ${kpi.bgColor}`}>
                      <kpi.icon className={`w-4 h-4 md:w-5 md:h-5 ${kpi.color}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Charts Row */}
          <div className="grid lg:grid-cols-3 gap-6 mb-8">
            {/* Sales Chart */}
            <Card className="lg:col-span-2 border border-border/50" data-testid="sales-chart">
              <CardHeader className="pb-2">
                <CardTitle className="font-chivo text-lg">
                  Ventas {selectedPeriod === 'today' ? 'de Hoy' : selectedPeriod === 'week' ? 'de la Semana' : selectedPeriod === 'month' ? 'del Mes' : selectedPeriod === 'year' ? 'del Año' : 'Personalizadas'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                      <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => [`$${value}`, 'Ventas']}
                      />
                      <Bar dataKey="ventas" fill="#f97316" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Sales by Type */}
            <Card className="border border-border/50" data-testid="sales-by-type">
              <CardHeader className="pb-2">
                <CardTitle className="font-chivo text-lg">
                  Ventas por Tipo {selectedPeriod === 'today' ? '(Hoy)' : selectedPeriod === 'week' ? '(Semana)' : selectedPeriod === 'month' ? '(Mes)' : selectedPeriod === 'year' ? '(Año)' : ''}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[160px] mb-4">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={salesByType}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={70}
                        paddingAngle={2}
                        dataKey="total"
                        nameKey="label"
                      >
                        {salesByType.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [`$${value}`, '']} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-2">
                  {salesByType.map((type, idx) => (
                    <div key={type.type} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx] }} />
                        <span>{type.label}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-muted-foreground">{type.count} órdenes</span>
                        <span className="font-bold">${type.total.toLocaleString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Bottom Row */}
          <div className="grid lg:grid-cols-3 gap-6 mb-8">
            {/* Top Products */}
            <Card className="border border-border/50" data-testid="top-products">
              <CardHeader className="pb-2">
                <CardTitle className="font-chivo text-lg">Productos Top</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {topProducts.length > 0 ? topProducts.map((product, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="w-7 h-7 rounded-lg bg-orange-500/10 flex items-center justify-center text-orange-500 font-bold text-sm">
                          {idx + 1}
                        </span>
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate max-w-[120px]">{product.name}</p>
                          <p className="text-xs text-muted-foreground">{product.quantity} vendidos</p>
                        </div>
                      </div>
                      <span className="text-sm font-bold text-green-600">
                        ${product.revenue?.toLocaleString() || 0}
                      </span>
                    </div>
                  )) : (
                    <p className="text-muted-foreground text-sm text-center py-8">
                      No hay datos de ventas aún
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Recent Orders */}
            <Card className="lg:col-span-2 border border-border/50" data-testid="recent-orders">
              <CardHeader className="pb-2">
                <CardTitle className="font-chivo text-lg flex items-center justify-between">
                  Órdenes Recientes
                  <Link to="/pos" className="text-sm font-normal text-orange-500 hover:underline">
                    Ver todas
                  </Link>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {recentOrders.length > 0 ? recentOrders.map((order) => (
                    <div key={order.order_id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-background rounded-lg">
                          {getOrderTypeIcon(order.order_type)}
                        </div>
                        <div>
                          <p className="font-medium text-sm">
                            #{order.order_number}
                            {order.table_number && <span className="text-muted-foreground"> • Mesa {order.table_number}</span>}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Clock className="w-3 h-3" />
                            {formatTime(order.created_at)}
                            {order.customer_name && <span>• {order.customer_name}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-sm">${order.total.toLocaleString()}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(order.status)}`}>
                          {getStatusLabel(order.status)}
                        </span>
                      </div>
                    </div>
                  )) : (
                    <p className="text-muted-foreground text-sm text-center py-8">
                      No hay órdenes recientes
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Intelligent Alerts Section */}
          {advancedStats && (advancedStats.alerts.trending_products.length > 0 || advancedStats.alerts.declining_products.length > 0 || advancedStats.alerts.low_stock_top_sellers.length > 0) && (
            <div className="mb-8">
              <h2 className="font-chivo font-bold text-lg mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-500" />
                Alertas Inteligentes
              </h2>
              <div className="grid lg:grid-cols-3 gap-6">
                {/* Trending Products */}
                {advancedStats.alerts.trending_products.length > 0 && (
                  <Card className="border border-green-200 bg-green-50/50">
                    <CardHeader className="pb-2">
                      <CardTitle className="font-chivo text-base flex items-center gap-2 text-green-700">
                        <TrendingUp className="w-4 h-4" />
                        Productos en Tendencia
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {advancedStats.alerts.trending_products.map((product, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-white rounded-lg">
                            <span className="text-sm font-medium truncate max-w-[140px]">{product.name}</span>
                            <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full font-bold">
                              +{product.change_percent}%
                            </span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Declining Products */}
                {advancedStats.alerts.declining_products.length > 0 && (
                  <Card className="border border-red-200 bg-red-50/50">
                    <CardHeader className="pb-2">
                      <CardTitle className="font-chivo text-base flex items-center gap-2 text-red-700">
                        <TrendingDown className="w-4 h-4" />
                        Productos Bajando
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {advancedStats.alerts.declining_products.map((product, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-white rounded-lg">
                            <span className="text-sm font-medium truncate max-w-[140px]">{product.name}</span>
                            <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded-full font-bold">
                              {product.change_percent}%
                            </span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Low Stock Alerts */}
                {advancedStats.alerts.low_stock_top_sellers.length > 0 && (
                  <Card className="border border-amber-200 bg-amber-50/50">
                    <CardHeader className="pb-2">
                      <CardTitle className="font-chivo text-base flex items-center gap-2 text-amber-700">
                        <AlertTriangle className="w-4 h-4" />
                        Stock Bajo (Top Ventas)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {advancedStats.alerts.low_stock_top_sellers.map((product, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-white rounded-lg">
                            <span className="text-sm font-medium truncate max-w-[140px]">{product.name}</span>
                            <span className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded-full font-bold">
                              {product.stock} uds
                            </span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          )}

          {/* Best Day Card */}
          {advancedStats?.insights?.best_day && (
            <Card className="border border-border/50 mb-8">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-lg bg-indigo-500/10">
                      <Target className="w-6 h-6 text-indigo-500" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Mejor día de la semana</p>
                      <p className="text-xl font-chivo font-bold">{advancedStats.insights.best_day}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-chivo font-extrabold text-green-600">
                      ${advancedStats.insights.best_day_sales?.toLocaleString() || 0}
                    </p>
                    <p className="text-xs text-muted-foreground">en ventas</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick Actions */}
          <h2 className="font-chivo font-bold text-lg mb-4">Acciones Rápidas</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link to="/pos">
              <Card className="border border-border/50 hover:border-orange-500 hover:shadow-lg transition-all cursor-pointer group" data-testid="quick-action-pos">
                <CardContent className="p-4 md:p-6 flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-orange-500/10 group-hover:bg-orange-500/20 transition-colors">
                    <ShoppingCart className="w-5 h-5 md:w-6 md:h-6 text-orange-500" />
                  </div>
                  <div>
                    <p className="font-bold text-sm md:text-base">Nueva Venta</p>
                    <p className="text-xs md:text-sm text-muted-foreground">Ir al POS</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
            
            <Link to="/kitchen-monitor" target="_blank">
              <Card className="border border-border/50 hover:border-orange-500 hover:shadow-lg transition-all cursor-pointer group" data-testid="quick-action-kitchen">
                <CardContent className="p-4 md:p-6 flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-red-500/10 group-hover:bg-red-500/20 transition-colors">
                    <ChefHat className="w-5 h-5 md:w-6 md:h-6 text-red-500" />
                  </div>
                  <div>
                    <p className="font-bold text-sm md:text-base">Cocina</p>
                    <p className="text-xs md:text-sm text-muted-foreground">Monitor KDS</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
            
            <Link to="/tables">
              <Card className="border border-border/50 hover:border-orange-500 hover:shadow-lg transition-all cursor-pointer group" data-testid="quick-action-tables">
                <CardContent className="p-4 md:p-6 flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-green-500/10 group-hover:bg-green-500/20 transition-colors">
                    <Grid3X3 className="w-5 h-5 md:w-6 md:h-6 text-green-500" />
                  </div>
                  <div>
                    <p className="font-bold text-sm md:text-base">Mesas</p>
                    <p className="text-xs md:text-sm text-muted-foreground">{stats?.occupied_tables || 0}/{stats?.total_tables || 0} ocupadas</p>
                  </div>
                </CardContent>
              </Card>
            </Link>

            <Link to="/ai-insights">
              <Card className="border border-border/50 hover:border-orange-500 hover:shadow-lg transition-all cursor-pointer group" data-testid="quick-action-ai">
                <CardContent className="p-4 md:p-6 flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-purple-500/10 group-hover:bg-purple-500/20 transition-colors">
                    <Brain className="w-5 h-5 md:w-6 md:h-6 text-purple-500" />
                  </div>
                  <div>
                    <p className="font-bold text-sm md:text-base">IA Insights</p>
                    <p className="text-xs md:text-sm text-muted-foreground">Predicciones</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
